// What the tier ACTUALLY serves, read from the consumer endpoint as a consumer.
//
// Every other figure on these screens is DESIRED state: the revision an
// operator published, the pointer the registry holds, the draft awaiting
// review. None of it is evidence that the serving tier returns that revision.
// Until this module existed the console had no channel that could tell the
// difference, so it printed the published revision under the heading "what
// devices render now" — a claim about the world made from a record of an
// intention.
//
// This issues `GET /v2/control-plane/products/:productId` with no credentials,
// which is the exact request a device makes. Reading the same route the same
// way is what makes the answer an OBSERVATION rather than a restatement of
// something we already believed.
//
// WHAT THIS OBSERVES, AND WHAT IT DOES NOT. It observes the serving tier the
// console is pointed at (`API_BASE`). It is NOT a report from an installed
// app: no Gao client reports the revision it loaded, so device-level truth
// stays unobserved and nothing built on this module may be labelled as
// covering it. `cache: "no-store"` stops the browser answering from its own
// cache; an intermediary honouring the route's `max-age=300` may still return
// a document up to five minutes old, so a just-published revision can read as
// divergent for that window. That reading is correct — propagation has a
// duration, and hiding it would restore exactly the false certainty this
// module exists to remove.

import { API_BASE } from "../config";

/** What the serving tier returned when asked for this product's document.
 *
 *  `not_serving` carries the server's own sentence rather than a re-coded
 *  enum. The route answers 404 for three distinct conditions — unknown
 *  product, disabled product, no published revision — that are distinguished
 *  only by prose, and a local enum matched against that prose would silently
 *  mis-classify the day the server rephrases one. The operator reads what the
 *  server said. */
export type Observation =
  | { kind: "serving"; revision: string; publishedAt: string; productId: string }
  | { kind: "not_serving"; reason: string }
  | { kind: "unavailable"; detail: string };

/** The `{ error, message }` shape every refusal on this route carries. */
function messageOf(body: unknown): string | null {
  if (body === null || typeof body !== "object") return null;
  const m = (body as { message?: unknown }).message;
  return typeof m === "string" && m !== "" ? m : null;
}

export function observedPath(productId: string): string {
  return `/v2/control-plane/products/${encodeURIComponent(productId)}`;
}

/**
 * Ask the serving tier what it is serving for `productId`.
 *
 * Never throws: an observation that failed is itself a state the operator
 * needs to see. A thrown error here would surface as a page-level failure and
 * the screen would fall back to showing desired state alone — which is the
 * behaviour being corrected.
 */
export async function observeServedRevision(
  productId: string,
  opts: { fetcher?: typeof fetch } = {},
): Promise<Observation> {
  const f = opts.fetcher ?? fetch;

  let res: Response;
  try {
    res = await f(`${API_BASE}${observedPath(productId)}`, {
      method: "GET",
      // The consumer route is unauthenticated. Sending the operator's bearer
      // would observe a request no device ever makes.
      credentials: "omit",
      cache: "no-store",
      headers: { accept: "application/json" },
    });
  } catch (e) {
    return {
      kind: "unavailable",
      detail: e instanceof Error ? e.message : String(e),
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (res.status === 200) {
    const d = body as Record<string, unknown> | null;
    if (
      d === null ||
      typeof d.revision !== "string" ||
      typeof d.publishedAt !== "string" ||
      typeof d.productId !== "string"
    ) {
      return {
        kind: "unavailable",
        detail: "the tier returned 200 with no readable revision",
      };
    }
    // One app must never receive another app's configuration. The console is
    // the only place that asks for a named product and can also see what came
    // back, so it is the only place that can notice. Treated as unavailable
    // rather than compared: a document for the wrong product tells us nothing
    // about this one.
    if (d.productId !== productId) {
      return {
        kind: "unavailable",
        detail: `the tier served productId "${d.productId}" for a request for "${productId}"`,
      };
    }
    return {
      kind: "serving",
      revision: d.revision,
      publishedAt: d.publishedAt,
      productId: d.productId,
    };
  }

  if (res.status === 404) {
    return { kind: "not_serving", reason: messageOf(body) ?? "the tier returned 404" };
  }

  return {
    kind: "unavailable",
    detail: messageOf(body) ?? `the tier returned HTTP ${res.status}`,
  };
}
